import { ActiveProfile, ActiveLanguageId, SetActiveLanguage } from 'src/ConsentManagement/Config/ActiveProfile';
import { ContentService } from 'src/ConsentManagement/ContentService';
import { GetTemplate, GetStyle } from '../Repositories/AssetRepositoryAsync';

const parser = new DOMParser();

const Constants = {
  Style: {
    URL: 'language-switcher',
  },
  Templates: {
    URL: 'consent-management/language-switcher-components',
    SWITCHER_ID: '#LanguageSwticherTemplate',
  },
  Switcher: {
    ID: '#GDPRLanguageSwitcher',
    ROOT_ELEMENT_ID: '#GDPRLanguageSwitcherContainer',
    TITLE_SELECTOR: '[language-switcher-title]',
    OPTIONS_TOGGLE_SELECTOR: '.accordion-toggle',
  },
  Options: {
    OPTIONS_CONTAINER_ID: '#GDPRLanguageSwitcherOptions',
    OPTION_DISPLAY_NAME_SELECTOR: '[data-language-display-name]',
    ITEM_SELECTOR: '#LanguageSwitcherOptionsTemplate > *:first-child',
  },
} as const;

class LanguageSwitcherView {
  private fetchLanguagePromise: Promise<any>;
  private languageSwitcher: HTMLElement;

  private updateSelectedLanguageText(selectedLanguageId?: string) {
    const activeLanguageDisplayname = ActiveProfile.SupportedLanguages[selectedLanguageId ?? ActiveLanguageId];
    this.languageSwitcher.querySelector(Constants.Switcher.TITLE_SELECTOR).innerHTML = activeLanguageDisplayname;
  }

  private onOptionsShown: EventListener = async (evt: Event) => {
    evt.preventDefault();

    const toggle = evt.currentTarget as HTMLInputElement;

    if (!toggle.checked) return;

    this.fetchLanguagePromise = ContentService.PrefetchAllLanguages();
  };

  private onOptionSelected: EventListener = async (clickEvent: Event) => {
    clickEvent.preventDefault();

    const option = clickEvent.currentTarget as HTMLElement;

    const selectedLanguageId = option.dataset.optionId;

    this.updateSelectedLanguageText(selectedLanguageId);
    option.querySelector('input').checked = true;
    const toggle : HTMLInputElement =  this.languageSwitcher.querySelector('.accordion-toggle');
    toggle.checked = false;
    await this.fetchLanguagePromise;

    SetActiveLanguage(selectedLanguageId);
  };

  private async getHtmlTemplates() {
    const templateString = await GetTemplate(Constants.Templates.URL);
    const templateComponents = parser.parseFromString(templateString, 'text/html');

    const getComponent = (qs: string) => templateComponents.querySelector(qs).cloneNode(true) as HTMLElement;

    return {
      languageSwitcher: getComponent(Constants.Switcher.ID),
      optionItem: getComponent(Constants.Options.ITEM_SELECTOR),
    };
  }

  private async buildLanguageSwitcher() {
    const styleRequest = GetStyle(Constants.Style.URL);

    const { languageSwitcher, optionItem } = await this.getHtmlTemplates();

    this.languageSwitcher = languageSwitcher;

    const toggleButton = languageSwitcher.querySelector(Constants.Switcher.OPTIONS_TOGGLE_SELECTOR);
    const optionsContainer = languageSwitcher.querySelector(Constants.Options.OPTIONS_CONTAINER_ID);

    for (const languageId in ActiveProfile.SupportedLanguages)
      optionsContainer.appendChild(this.createOptionItem(optionItem, languageId));

    toggleButton.addEventListener('change', this.onOptionsShown);

    languageSwitcher.querySelector('style').innerHTML = await styleRequest;

    this.updateSelectedLanguageText();
  }

  private createOptionItem(optionItemTemplate: any, languageId: string) {
    const option = optionItemTemplate.cloneNode(true) as HTMLElement;

    option.dataset.optionId = languageId;
    option.querySelector(Constants.Options.OPTION_DISPLAY_NAME_SELECTOR).innerHTML =
      ActiveProfile.SupportedLanguages[languageId];
    option.querySelector('input').checked = languageId === ActiveLanguageId;
    option.addEventListener('click', this.onOptionSelected);

    return option;
  }

  public async build(): Promise<void> {
    const hasMultipleLanguages =
      !!ActiveProfile.SupportedLanguages && Object.keys(ActiveProfile.SupportedLanguages).length >= 2;

    if (!hasMultipleLanguages) return;

    await this.buildLanguageSwitcher();

    const container = document.querySelector(Constants.Switcher.ROOT_ELEMENT_ID);
    container.appendChild(this.languageSwitcher);
  }
}

export { LanguageSwitcherView };
